import { forwardRef } from 'react';
import { BracketPicks } from '@/types/bracket';
import { BracketSlot } from '@/hooks/use-bracket';

interface BracketCaptureProps {
  picks: BracketPicks;
  bracketSlots: BracketSlot[];
  displayName: string;
  avatarId: number | null;
  inviteCode: string | null;
}

// Round labels (Hebrew) — mirrors BracketView constants
const ROUND_LABELS: Record<string, string> = {
  round1: 'סיבוב ראשון',
  first_round: 'סיבוב ראשון',
  west_semis: 'סיבוב שני – מערב',
  east_semis: 'סיבוב שני – מזרח',
  west_finals: 'גמר מזרחי',
  east_finals: 'גמר מזרחי',
  finals: 'הגמר הגדול',
};

// Canonical round order for display
const ROUND_ORDER = [
  'round1',
  'first_round',
  'west_semis',
  'east_semis',
  'west_finals',
  'east_finals',
  'finals',
];

// Avatar background colors keyed by avatarId mod
const AVATAR_COLORS = [
  'hsl(24, 95%, 53%)',
  'hsl(200, 80%, 45%)',
  'hsl(160, 60%, 40%)',
  'hsl(280, 60%, 50%)',
  'hsl(340, 70%, 50%)',
];

function getAvatarColor(avatarId: number | null, displayName: string): string {
  if (avatarId !== null) return AVATAR_COLORS[avatarId % AVATAR_COLORS.length];
  const code = displayName.charCodeAt(0) || 0;
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

// Group slots by round, returning only rounds that have picks
function getPicksByRound(
  picks: BracketPicks,
  bracketSlots: BracketSlot[],
): { round: string; label: string; rows: { teamName: string; score: string | null }[] }[] {
  // Build slotId → slot map
  const slotMap = new Map<string, BracketSlot>();
  bracketSlots.forEach(s => slotMap.set(s.slotId, s));

  // Collect picks per round (in ROUND_ORDER)
  const roundMap = new Map<string, { teamName: string; score: string | null }[]>();

  Object.entries(picks).forEach(([slotId, pick]) => {
    const slot = slotMap.get(slotId);
    if (!slot) return;
    const round = slot.round;
    if (!roundMap.has(round)) roundMap.set(round, []);
    roundMap.get(round)!.push({
      teamName: pick.winner,
      score: pick.predicted_score ?? null,
    });
  });

  // Return in ROUND_ORDER, deduplicated by round key (round1/first_round both map to same label)
  const seen = new Set<string>();
  const result: { round: string; label: string; rows: { teamName: string; score: string | null }[] }[] = [];

  ROUND_ORDER.forEach(roundKey => {
    if (seen.has(roundKey)) return;
    const rows = roundMap.get(roundKey);
    if (!rows || rows.length === 0) return;

    // Deduplicate display: round1 and first_round share the same label
    const label = ROUND_LABELS[roundKey] || roundKey;
    seen.add(roundKey);
    // Also mark the sibling so we don't double-render
    if (roundKey === 'round1') seen.add('first_round');
    if (roundKey === 'first_round') seen.add('round1');

    // Merge if sibling also has rows
    const siblingKey = roundKey === 'round1' ? 'first_round' : roundKey === 'first_round' ? 'round1' : null;
    const siblingRows = siblingKey ? (roundMap.get(siblingKey) ?? []) : [];

    result.push({ round: roundKey, label, rows: [...rows, ...siblingRows] });
  });

  return result;
}

const BracketCapture = forwardRef<HTMLDivElement, BracketCaptureProps>(
  ({ picks, bracketSlots, displayName, avatarId, inviteCode }, ref) => {
    const initial = displayName.charAt(0).toUpperCase();
    const avatarColor = getAvatarColor(avatarId, displayName);
    const roundGroups = getPicksByRound(picks, bracketSlots);

    return (
      <div
        ref={ref}
        style={{
          position: 'fixed',
          left: '-9999px',
          top: 0,
          width: '1080px',
          pointerEvents: 'none',
          fontFamily: 'Leon, AOTSerialMono, Inter, system-ui, sans-serif',
          direction: 'rtl',
          background: 'hsl(0, 0%, 5%)',
          color: '#fff',
          padding: '48px 56px',
          borderRadius: '24px',
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            marginBottom: '40px',
          }}
        >
          {/* Avatar circle */}
          <div
            style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              background: avatarColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              fontWeight: 700,
              color: '#fff',
              flexShrink: 0,
            }}
          >
            {initial}
          </div>

          {/* Name + title */}
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: '28px',
                fontWeight: 700,
                letterSpacing: '-0.025em',
              }}
            >
              <span style={{ color: '#fff' }}>BRACKET</span>{' '}<span style={{ color: 'hsl(24, 95%, 53%)' }}>RUN</span>
            </div>
            <div
              style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#fff',
                marginTop: '2px',
              }}
            >
              {displayName}
            </div>
          </div>

          {/* Season label */}
          <div
            style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.2em',
              textAlign: 'left',
            }}
          >
            NBA Playoffs 2026
          </div>
        </div>

        {/* Picks grid grouped by round */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {roundGroups.map(({ round, label, rows }) => (
            <div key={round}>
              {/* Round header */}
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.2em',
                  color: 'hsl(24, 95%, 53%)',
                  marginBottom: '10px',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                  paddingBottom: '6px',
                }}
              >
                {label}
              </div>

              {/* Pick rows in a 2-column grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '8px',
                }}
              >
                {rows.map((row, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 16px',
                      background: 'hsl(0, 0%, 8%)',
                      borderRadius: '10px',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: '#fff',
                      }}
                    >
                      {row.teamName}
                    </span>
                    {row.score && (
                      <span
                        style={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: 'rgba(255,255,255,0.55)',
                          fontVariantNumeric: 'tabular-nums',
                          marginRight: '8px',
                        }}
                      >
                        {row.score}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: '40px',
            paddingTop: '20px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {inviteCode && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
              }}
            >
              <span style={{ fontSize: '15px', color: '#fff', fontWeight: 500 }}>
                הצטרף עכשיו!
              </span>
              <span
                style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: 'hsl(24, 95%, 53%)',
                  fontFamily: 'monospace',
                  letterSpacing: '0.12em',
                  textAlign: 'left',
                }}
              >
                {inviteCode}
              </span>
            </div>
          )}

          {/* Watermark */}
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', margin: 0, textAlign: 'center' }}>
            <span style={{ fontWeight: 700 }}>BRACKET</span>{' '}<span style={{ color: 'hsl(24, 95%, 53%)', fontWeight: 700 }}>RUN</span>
            {' — '}
            משחק ניחושי פלייאוף NBA
          </p>
        </div>
      </div>
    );
  }
);

BracketCapture.displayName = 'BracketCapture';

export default BracketCapture;
